import React, { useState, useEffect } from 'react';
import { Link2, Plus, Trash2, ExternalLink, Copy, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminNotesTab() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const loadNotes = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/notes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotes(data.notes || []);
      }
    } catch (err) {
      console.error('Failed to load notes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadNotes(); }, []);

  const handleAdd = async () => {
    if (!newUrl.trim()) {
      toast.error('URL requise');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/notes`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          url: newUrl.trim(),
          email: newEmail.trim(),
          note: newNote.trim()
        })
      });
      if (res.ok) {
        toast.success('Note ajoutée');
        setNewUrl('');
        setNewEmail('');
        setNewNote('');
        setShowForm(false);
        loadNotes();
      } else {
        toast.error('Erreur lors de l\'ajout');
      }
    } catch (err) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Supprimer cette note ?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/notes/${noteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Note supprimée');
        loadNotes();
      }
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success('Copié !');
  };

  if (loading) {
    return <div className="text-center py-12 text-[#5A8BA8]">Chargement...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#033871]">Notes de réattribution</h2>
          <p className="text-sm text-[#5A8BA8]">Sauvegardez des URLs pour retrouver les annonces à réattribuer</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-[#0C63BF] hover:bg-[#0952a0] text-white"
          data-testid="add-note-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Ajouter
        </Button>
      </div>

      {/* Add form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-[#E8F4FD] p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-[#033871] mb-1">URL de l'annonce *</label>
            <input
              type="url"
              value={newUrl}
              onChange={e => setNewUrl(e.target.value)}
              placeholder="https://www.leboncoin.fr/..."
              className="w-full h-10 px-3 rounded-lg border border-[#E8F4FD] text-sm focus:border-[#0C63BF] outline-none"
              data-testid="note-url-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#033871] mb-1">Email du vendeur</label>
            <input
              type="email"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              placeholder="vendeur@email.com"
              className="w-full h-10 px-3 rounded-lg border border-[#E8F4FD] text-sm focus:border-[#0C63BF] outline-none"
              data-testid="note-email-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#033871] mb-1">Note</label>
            <textarea
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              placeholder="Babolat Viper Air - 150€ - Contacter pour réattribution..."
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-[#E8F4FD] text-sm focus:border-[#0C63BF] outline-none resize-none"
              data-testid="note-text-input"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setShowForm(false)} className="text-[#5A8BA8]">
              Annuler
            </Button>
            <Button onClick={handleAdd} disabled={saving} className="bg-[#32CD32] hover:bg-[#28a428] text-white">
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      )}

      {/* Notes list */}
      {notes.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#E8F4FD] p-8 text-center">
          <Link2 className="w-12 h-12 text-[#D6E8F0] mx-auto mb-3" />
          <p className="text-[#5A8BA8] font-medium">Aucune note</p>
          <p className="text-xs text-[#B8D4E3] mt-1">Ajoutez des URLs d'annonces à réattribuer</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map(note => (
            <div key={note.id} className="bg-white rounded-xl border border-[#E8F4FD] p-4" data-testid={`note-${note.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  {/* URL */}
                  <div className="flex items-center gap-2 mb-2">
                    <Link2 className="w-4 h-4 text-[#0C63BF] shrink-0" />
                    <a 
                      href={note.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-[#0C63BF] hover:underline truncate"
                    >
                      {note.url}
                    </a>
                    <a 
                      href={note.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-1 hover:bg-[#F0F7FA] rounded shrink-0"
                    >
                      <ExternalLink className="w-3.5 h-3.5 text-[#5A8BA8]" />
                    </a>
                  </div>
                  
                  {/* Email */}
                  {note.email && (
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-[#5A8BA8]">Email:</span>
                      <span className="text-sm font-medium text-[#033871]">{note.email}</span>
                      <button 
                        onClick={() => copyToClipboard(note.email, note.id)}
                        className="p-1 hover:bg-[#F0F7FA] rounded"
                      >
                        {copiedId === note.id ? (
                          <Check className="w-3.5 h-3.5 text-[#32CD32]" />
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-[#5A8BA8]" />
                        )}
                      </button>
                    </div>
                  )}
                  
                  {/* Note */}
                  {note.note && (
                    <p className="text-sm text-[#5A8BA8] bg-[#F0F7FA] rounded-lg px-3 py-2 mt-2">
                      {note.note}
                    </p>
                  )}
                  
                  {/* Date */}
                  <p className="text-[10px] text-[#B8D4E3] mt-2">
                    Ajouté le {new Date(note.created_at).toLocaleDateString('fr-FR', { 
                      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                    })}
                  </p>
                </div>
                
                {/* Delete button */}
                <button 
                  onClick={() => handleDelete(note.id)}
                  className="p-2 hover:bg-red-50 rounded-lg text-red-400 hover:text-red-500 transition-colors shrink-0"
                  data-testid={`delete-note-${note.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
